import React from "react";

/**
 * The Revenue Path™ — the brand's signature SVG asset. Renders the customer
 * journey as a continuous path of nodes whose STATE is legible without a legend
 * via line continuity, weight, node structure and the champagne signal color.
 *
 * States: "healthy" | "friction" | "leak" | "broken" | "recovered"
 * The connector INTO each node reflects that node's state.
 *
 * Ink line = the path. Champagne = signal (active/recovered/data).
 * Leaks show a gap + a dropped-opportunity marker falling away.
 *
 * `theme` ("light" | "dark") adapts the palette so the same asset can sit on
 * the warm sand base or a dark "operating map" surface.
 */

const CHAMPAGNE = "#C2A36B";

const PALETTE = {
  light: {
    ink: "#0A0A0B",
    muted: "#B8B1A2",
    baseline: "#E6E2D5",
    label: "#171717",
    index: "#8E887F",
    ivory: "#F9F8F3",
  },
  dark: {
    ink: "#C9C3B6",
    muted: "#6F6A61",
    baseline: "#33302B",
    label: "#F4F0E8",
    index: "#8E887F",
    ivory: "#1A1A18",
  },
};

function nodeConfig(p) {
  return {
    healthy: { r: 5, fill: p.ink, stroke: p.ink, ring: false },
    friction: { r: 5, fill: p.ivory, stroke: p.ink, ring: false },
    leak: { r: 5.5, fill: p.ivory, stroke: CHAMPAGNE, ring: false },
    broken: { r: 5, fill: p.ivory, stroke: p.muted, ring: false },
    recovered: { r: 6, fill: CHAMPAGNE, stroke: CHAMPAGNE, ring: true },
  };
}

function connectorProps(state, p) {
  switch (state) {
    case "friction":
      return { stroke: p.ink, width: 1.25, dash: "1 5", opacity: 0.55 };
    case "leak":
      return { stroke: CHAMPAGNE, width: 1.5, dash: "10 9", opacity: 0.7, gap: true };
    case "broken":
      return { stroke: p.muted, width: 1, dash: "2 8", opacity: 0.5, gap: true };
    case "recovered":
      return { stroke: CHAMPAGNE, width: 2.25, dash: null, opacity: 1 };
    default:
      return { stroke: p.ink, width: 1.75, dash: null, opacity: 0.9 };
  }
}

export default function RevenuePath({ stages, className = "", animate = true, theme = "light" }) {
  const p = PALETTE[theme] || PALETTE.light;
  const NODE = nodeConfig(p);
  const W = 1000;
  const H = 150;
  const padX = 60;
  const midY = 78;
  const n = stages.length;
  const step = (W - padX * 2) / (n - 1);
  const x = (i) => padX + step * i;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={`w-full h-auto ${className}`}
      role="img"
      aria-label="The Revenue Path: customer journey from attention to revenue with leak and recovered points"
      data-testid="revenue-path"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* baseline grid annotation */}
      <line x1={padX} y1={midY} x2={W - padX} y2={midY} stroke={p.baseline} strokeWidth="1" />

      {/* connectors between nodes (into node i) */}
      {stages.slice(1).map((s, idx) => {
        const i = idx + 1;
        const c = connectorProps(s.state, p);
        const x1 = x(i - 1);
        const x2 = x(i);
        if (c.gap) {
          // draw two stubs with a visible break in the middle
          const gap = 26;
          return (
            <g key={`c-${i}`}>
              <line x1={x1} y1={midY} x2={(x1 + x2) / 2 - gap / 2} y2={midY}
                stroke={c.stroke} strokeWidth={c.width} strokeDasharray={c.dash || undefined}
                opacity={c.opacity} strokeLinecap="round" />
              <line x1={(x1 + x2) / 2 + gap / 2} y1={midY} x2={x2} y2={midY}
                stroke={c.stroke} strokeWidth={c.width} strokeDasharray={c.dash || undefined}
                opacity={c.opacity} strokeLinecap="round" />
              {/* dropped opportunity marker falling from the break */}
              <circle cx={(x1 + x2) / 2} cy={midY + 22} r="2.5" fill={CHAMPAGNE} opacity="0.85" />
              <circle cx={(x1 + x2) / 2 + 5} cy={midY + 34} r="1.8" fill={CHAMPAGNE} opacity="0.5" />
            </g>
          );
        }
        return (
          <line
            key={`c-${i}`}
            x1={x1} y1={midY} x2={x2} y2={midY}
            stroke={c.stroke} strokeWidth={c.width}
            strokeDasharray={c.dash || undefined}
            opacity={c.opacity}
            strokeLinecap="round"
            className={animate && !c.dash ? `mx-path mx-path--${Math.min(i, 4)}` : undefined}
            pathLength={animate && !c.dash ? 1 : undefined}
          />
        );
      })}

      {/* nodes + labels */}
      {stages.map((s, i) => {
        const cfg = NODE[s.state] || NODE.healthy;
        return (
          <g key={`n-${i}`} data-testid={`revenue-node-${s.state}`}>
            {cfg.ring && (
              <circle cx={x(i)} cy={midY} r={cfg.r + 5} fill="none" stroke={CHAMPAGNE} strokeWidth="1" opacity="0.4" />
            )}
            <circle cx={x(i)} cy={midY} r={cfg.r} fill={cfg.fill} stroke={cfg.stroke} strokeWidth="1.5" />
            {/* stage index */}
            <text x={x(i)} y={midY - 20} textAnchor="middle"
              fontFamily="'JetBrains Mono', monospace" fontSize="10"
              fill={s.state === "recovered" || s.state === "leak" ? CHAMPAGNE : p.index}
              letterSpacing="1">
              {String(i + 1).padStart(2, "0")}
            </text>
            {/* stage label */}
            <text x={x(i)} y={midY + 34} textAnchor="middle"
              fontFamily="'Manrope', sans-serif" fontSize="11" fontWeight="600"
              fill={p.label}>
              {s.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
